import React from "react";
import { observer } from "mobx-react-lite";
import { Alert, Col, DatePicker, Form, Input, Row, Select, Upload } from "antd";
import { FormInstance } from "antd/es/form";
import { Utils } from "../../common/misc/Utils";
import {
    FollowUpProject,
    FollowUpTaskOptions,
} from "../services/FollowUpTaskService";
import FollowUpTaskCreationStore, {
    FollowUpTaskValues,
} from "../stores/FollowUpTaskCreationStore";

const WorkbenchFollowUpTaskForm: React.FC<{
    form: FormInstance<FollowUpTaskValues>;
    options: FollowUpTaskOptions;
    project: FollowUpProject | undefined;
    requiresType: boolean;
    creationStore: FollowUpTaskCreationStore;
}> = ({ form, options, project, requiresType, creationStore }) => {
    const { files, saving, recovering } = creationStore;

    return (
        <Form
            form={form}
            layout="vertical"
            disabled={saving || recovering}
            preserve
        >
            <Form.Item
                label={<span className="dialog-field-label">Project</span>}
                name="projectId"
                rules={[{ required: true }]}
            >
                <Select
                    placeholder="Select a project"
                    options={options.projects.map((x) => ({
                        value: x.id,
                        label: x.name,
                    }))}
                    onChange={() =>
                        form.setFieldsValue({
                            assignedTo: undefined,
                            taskType: undefined,
                        })
                    }
                />
            </Form.Item>
            <Form.Item
                label={<span className="dialog-field-label">Title</span>}
                name="title"
                rules={[{ required: true, whitespace: true }]}
            >
                <Input
                    prefix={`${options.titlePrefix} —`}
                    placeholder="What needs to be done?"
                />
            </Form.Item>
            <Form.Item label="Description" name="description">
                <Input.TextArea rows={4} />
            </Form.Item>
            {requiresType && project && (
                <Form.Item
                    label={
                        <span className="dialog-field-label">Task type</span>
                    }
                    name="taskType"
                    rules={[{ required: true }]}
                >
                    <Select
                        options={project.taskTypes.map((x) => ({
                            value: x.id,
                            label: x.name,
                        }))}
                    />
                </Form.Item>
            )}
            {project && !project.taskTypes.length && (
                <Alert
                    type="warning"
                    message="This project needs a task type with an enabled initial status. Contact a project administrator."
                />
            )}
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label="Assignee" name="assignedTo">
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Unassigned"
                            options={project?.assignees.map((x) => ({
                                value: x.id,
                                label: x.name,
                            }))}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Due date" name="dueDate">
                        <DatePicker />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item label="Attachments" htmlFor="follow-up-attachments">
                <Upload.Dragger
                    id="follow-up-attachments"
                    multiple
                    accept={Utils.uploadAcceptExtensions}
                    disabled={saving || recovering}
                    fileList={files}
                    beforeUpload={(file) => {
                        if (!Utils.isUploadAccepted(file.name)) {
                            creationStore.rejectFile(
                                `Unsupported file type: ${file.name}`,
                            );
                            return Upload.LIST_IGNORE;
                        }
                        creationStore.addFile(file);
                        return false;
                    }}
                    onRemove={(file) => creationStore.removeFile(file.uid)}
                >
                    Drop files here or browse
                </Upload.Dragger>
            </Form.Item>
        </Form>
    );
};

export default observer(WorkbenchFollowUpTaskForm);
